import React, { useEffect, useState, useCallback } from 'react';
import { Card, Row, Col, Statistic, Input, Button, Table, Tag, Typography, Space, message, Tooltip } from 'antd';
import { CopyOutlined, ShareAltOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import apiClient from '../../../shared/services/apiClient';
import { formatMoney } from '../../../shared/utils/money';

const { Text } = Typography;

const STATUS_COLOR = { pending: 'gold', credited: 'green', reversed: 'default' };

/** 推广返利：我的邀请链接、统计、佣金明细（规则见 api AffiliateService） */
const Affiliate = () => {
    const { t } = useTranslation();
    const [info, setInfo] = useState(null);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        apiClient.get('/api/user/affiliate').then((r) => r.data?.success && setInfo(r.data.data)).catch(() => {});
    }, []);

    const load = useCallback(async (p) => {
        setLoading(true);
        try {
            const r = await apiClient.get('/api/user/affiliate/commissions', { params: { page: p, page_size: 20 } });
            if (r.data?.success) {
                setRows(r.data.data.items || []);
                setTotal(r.data.data.total || 0);
            }
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(page); }, [page, load]);

    const link = info ? `https://yeap.cloud/?aff=${info.code}` : '';
    const copy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            message.success(t('Copied'));
        } catch (_) {
            message.error(t('Failed to copy. Please copy manually.'));
        }
    };
    // 卡片/分组按 USD 记（佣金都是 USD）
    const usd = (v) => formatMoney(v, 'USD', { original: true });

    const sourceLabel = (r) => (r.source_type === 'llm_transfer' ? t('LLM API') : t('Bill'));

    return (
        <div>
            <Card style={{ marginBottom: 16 }}>
                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                    <Space>
                        <ShareAltOutlined />
                        <Text strong>{t('Your referral link')}</Text>
                        {info && (
                            <Tooltip title={t('Earn {{rate}}% of what people you refer pay for {{months}} months. Commissions reach your balance after {{days}} days.', { rate: info.rate, months: info.months, days: info.hold_days })}>
                                <QuestionCircleOutlined style={{ color: 'var(--ant-color-text-tertiary, #999)' }} />
                            </Tooltip>
                        )}
                    </Space>
                    <Space.Compact style={{ width: '100%', maxWidth: 560 }}>
                        <Input readOnly value={link} />
                        <Button icon={<CopyOutlined />} onClick={() => copy(link)} disabled={!link}>{t('Copy')}</Button>
                    </Space.Compact>
                    {info && !info.enabled && <Text type="warning">{t('The referral program is paused')}</Text>}
                </Space>
            </Card>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={12} md={6}><Card><Statistic title={t('Referred users')} value={info?.referrals ?? 0} /></Card></Col>
                <Col xs={12} md={6}><Card><Statistic title={t('Paying users')} value={info?.paying_referrals ?? 0} /></Card></Col>
                <Col xs={12} md={6}><Card><Statistic title={t('Pending commission')} value={usd(info?.pending ?? 0)} /></Card></Col>
                <Col xs={12} md={6}><Card><Statistic title={t('Paid to balance')} value={usd(info?.credited ?? 0)} /></Card></Col>
            </Row>

            <Card title={t('Commissions')}>
                <Table
                    rowKey="id"
                    loading={loading}
                    dataSource={rows}
                    scroll={{ x: 640 }}
                    pagination={{ current: page, total, pageSize: 20, onChange: setPage, showSizeChanger: false }}
                    columns={[
                        { title: t('Time'), dataIndex: 'created_at', width: 170 },
                        { title: t('User'), dataIndex: 'referee', width: 100 },
                        { title: t('Source'), key: 'source', render: (_, r) => sourceLabel(r) },
                        { title: t('Amount paid'), dataIndex: 'base_amount', align: 'right', render: (v) => usd(v) },
                        { title: t('Rate'), dataIndex: 'rate', align: 'right', render: (v) => `${Number(v)}%` },
                        { title: t('Commission'), dataIndex: 'amount', align: 'right', render: (v) => <Text strong>{usd(v)}</Text> },
                        {
                            title: t('Status'), key: 'status', render: (_, r) => (
                                <Tooltip title={r.status === 'pending' ? t('Reaches your balance on {{date}}', { date: String(r.available_at).slice(0, 10) }) : null}>
                                    <Tag color={STATUS_COLOR[r.status]}>{t({ pending: 'Pending', credited: 'Paid', reversed: 'Reversed' }[r.status] || r.status)}</Tag>
                                </Tooltip>
                            ),
                        },
                    ]}
                />
            </Card>
        </div>
    );
};

export default Affiliate;
